// Ticket Responses Collection Service
import {
  collection,
  getDocs,
  addDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  onSnapshot,
} from 'firebase/firestore';
import type { Unsubscribe } from 'firebase/firestore';
import { db } from '../firebase/config';
import type { TicketResponse, Attachment } from '@/types';

const COLLECTION = 'ticketResponses';

export const ticketResponsesService = {
  // Get all responses for a ticket
  getByTicket: async (ticketId: string): Promise<TicketResponse[]> => {
    const q = query(
      collection(db, COLLECTION),
      where('ticketId', '==', ticketId),
      orderBy('createdAt', 'asc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as TicketResponse);
  },

  // Get non-internal responses (for public view)
  getPublicByTicket: async (ticketId: string): Promise<TicketResponse[]> => {
    const q = query(
      collection(db, COLLECTION),
      where('ticketId', '==', ticketId),
      where('isInternal', '==', false),
      orderBy('createdAt', 'asc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as TicketResponse);
  },

  // Subscribe to responses for a ticket (real-time)
  subscribe: (
    ticketId: string,
    callback: (responses: TicketResponse[]) => void,
    onError?: (error: Error) => void
  ): Unsubscribe => {
    const q = query(
      collection(db, COLLECTION),
      where('ticketId', '==', ticketId),
      orderBy('createdAt', 'asc')
    );
    return onSnapshot(
      q,
      (snapshot) => {
        callback(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as TicketResponse));
      },
      onError
    );
  },

  // Create a response (from agent)
  createAgentResponse: async (
    ticketId: string,
    orgId: string,
    projectId: string,
    content: string,
    authorId: string,
    authorName: string,
    authorEmail: string,
    isInternal: boolean,
    attachments: Attachment[] = []
  ): Promise<string> => {
    const docData = {
      ticketId,
      organizationId: orgId,
      projectId,
      content,
      attachments,
      authorId,
      authorName,
      authorEmail,
      isAgent: true,
      isInternal,
      sentViaEmail: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: authorId,
    };
    const docRef = await addDoc(collection(db, COLLECTION), docData);
    return docRef.id;
  },

  // Create a response (from end user via portal)
  createUserResponse: async (
    ticketId: string,
    orgId: string,
    projectId: string,
    content: string,
    userEmail: string,
    userName?: string,
    attachments: Attachment[] = []
  ): Promise<string> => {
    const docData = {
      ticketId,
      organizationId: orgId,
      projectId,
      content,
      attachments,
      authorId: userEmail, // Use email as ID for anonymous users
      authorName: userName || userEmail.split('@')[0],
      authorEmail: userEmail,
      isAgent: false,
      isInternal: false,
      sentViaEmail: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: userEmail,
    };
    const docRef = await addDoc(collection(db, COLLECTION), docData);
    return docRef.id;
  },

  // Generate a secure reply token for email links
  generateReplyToken: (ticketId: string): string => {
    // Create a URL-safe token combining ticket ID with timestamp
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substring(2, 10);
    return `${ticketId}_${timestamp}_${random}`;
  },

  // Parse reply token to get ticket ID
  parseReplyToken: (token: string): string | null => {
    const parts = token.split('_');
    if (parts.length < 3) return null;
    return parts[0];
  },
};
